import { useState, useEffect, useRef } from 'react';
import io from 'socket.io-client';
import useChatSocketToken from './useChatSocketToken';

const SOCKET_CONFIG = {
  RECONNECTION_ATTEMPTS: 5,
  RECONNECTION_DELAY: 1000,
  HEALTH_CHECK_INTERVAL: 30000,
};

/**
 * Hook for managing chat socket connection
 * @param {Object} params - Parameters
 * @param {string} params.conversationId - Conversation ID
 * @param {Object} params.userInfo - Current user info
 * @param {boolean} params.loading - User loading state
 * @param {boolean} params.otherUserLoading - Other user loading state
 * @returns {Object} Socket instance and connection status
 */
const useChatSocket = ({ conversationId, userInfo, loading, otherUserLoading }) => {
  const [socket, setSocket] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const { getChatSocketToken, tokenError } = useChatSocketToken();
  const socketRef = useRef(null);
  const currentConversationId = useRef(null);
  const isInitializing = useRef(false);

  useEffect(() => {
    // Wait for loading to complete
    if (loading || otherUserLoading) {
      return;
    }
    
    if (!conversationId || !userInfo) {
      return;
    }

    // Prevent re-initialization if socket already exists and conversation hasn't changed
    if (socketRef.current && socketRef.current.connected && currentConversationId.current === conversationId) {
      return;
    }

    // Prevent multiple simultaneous initializations
    if (isInitializing.current) {
      return;
    }

    // Clean up previous socket if conversation changed
    if (socketRef.current && currentConversationId.current !== conversationId) {
      socketRef.current.disconnect();
      socketRef.current = null;
      setSocket(null);
    }

    // Prevent re-initialization for same conversation
    if (currentConversationId.current === conversationId && socketRef.current) {
      return;
    }

    isInitializing.current = true;
    currentConversationId.current = conversationId;

    const initializeSocket = async () => {
      try {
        const token = await getChatSocketToken();

        if (!token) {
          setConnectionStatus('error');
          isInitializing.current = false;
          return;
        }

        const socketUrl = process.env.REACT_APP_CHAT_API_URL;
        
        if (!socketUrl) {
          setConnectionStatus('error');
          isInitializing.current = false;
          return;
        }
        
        const newSocket = io(socketUrl, {
          auth: { token },
          query: { 
            conversationId
          },
          transports: ['websocket'],
          secure: true,
          reconnection: true,
          reconnectionAttempts: SOCKET_CONFIG.RECONNECTION_ATTEMPTS,
          reconnectionDelay: SOCKET_CONFIG.RECONNECTION_DELAY,
          timeout: 10000,
        });

        newSocket.on('connect_error', (error) => {
          setConnectionStatus('error');
          isInitializing.current = false;
        });

        newSocket.on('connect', () => {
          setConnectionStatus('connected');
          newSocket.emit('joinConversation', conversationId);
          isInitializing.current = false;
        });

        newSocket.on('error', (error) => {
          setConnectionStatus('error');
          isInitializing.current = false;
        });

        newSocket.on('disconnect', (reason) => {
          setConnectionStatus('disconnected');
          isInitializing.current = false;
        });

        newSocket.on('unauthorized', (error) => {
          setConnectionStatus('error');
          isInitializing.current = false;
        });

        socketRef.current = newSocket;
        setSocket(newSocket);
      } catch (error) {
        setConnectionStatus('error');
        isInitializing.current = false;
      }
    };

    initializeSocket();

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
        isInitializing.current = false;
      }
    };
  }, [conversationId, userInfo?.userId, loading, otherUserLoading]);

  useEffect(() => {
    // Socket state tracking
  }, [socket?.connected, socket?.id]);

  return {
    socket,
    connectionStatus,
    tokenError
  };
};

export default useChatSocket;

